import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas-pro';
import { apiCall, ApiError } from '../utils/apiHelpers';

interface Stroke {
    color: string;
    size: number;
    points: Array<{ x: number; y: number }>;
}

interface Props {
    repoId: string;
    path: string;
    commitSha: string;
    /** The element that will be captured. The overlay is rendered as a sibling
     *  of this element and pointer events go through the SVG; text below stays
     *  inert. */
    targetRef: React.RefObject<HTMLElement | null>;
    onCancel: () => void;
    onSaved: (info: { drawingId: string; rawUrl: string; previewDataUrl: string }) => void;
}

const PALETTE = ['#ef4444', '#1d4ed8', '#10b981', '#f59e0b', '#000000'];

export function AnnotationOverlay({ repoId, path, commitSha, targetRef, onCancel, onSaved }: Props) {
    const svgRef = useRef<SVGSVGElement>(null);
    const [strokes, setStrokes] = useState<Stroke[]>([]);
    const [color, setColor] = useState(PALETTE[0]);
    const [size, setSize] = useState(3);
    const [drawing, setDrawing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dims, setDims] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    // Track the target's size so the SVG matches it exactly. Resize-observer
    // catches font loads, image loads, etc. that change the body height.
    useEffect(() => {
        const t = targetRef.current;
        if (!t) return;
        function measure() {
            const el = targetRef.current;
            if (!el) return;
            setDims({ width: el.offsetWidth, height: el.offsetHeight });
        }
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(t);
        window.addEventListener('resize', measure);
        // Clear the user's text selection — it interferes with drawing.
        try { window.getSelection()?.removeAllRanges(); } catch {}
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [targetRef]);

    function pointFromEvent(e: React.PointerEvent<SVGSVGElement>) {
        const r = e.currentTarget.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
        e.preventDefault();
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
        const pt = pointFromEvent(e);
        setDrawing(true);
        setStrokes((s) => [...s, { color, size, points: [pt] }]);
    }
    function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
        if (!drawing) return;
        e.preventDefault();
        const pt = pointFromEvent(e);
        setStrokes((s) => {
            const last = s[s.length - 1];
            if (!last) return s;
            return [...s.slice(0, -1), { ...last, points: [...last.points, pt] }];
        });
    }
    function onPointerUp(e: React.PointerEvent<SVGSVGElement>) {
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
        setDrawing(false);
    }

    function pathFor(s: Stroke): string {
        if (s.points.length === 0) return '';
        return s.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    }

    async function save() {
        const target = targetRef.current;
        if (!target) return;
        setSaving(true);
        setError(null);
        try {
            const snapshot = await html2canvas(target, {
                backgroundColor: '#ffffff',
                scale: window.devicePixelRatio > 1 ? 2 : 1.5,
                logging: false,
                useCORS: true,
                allowTaint: false
            });
            const composite = document.createElement('canvas');
            composite.width = snapshot.width;
            composite.height = snapshot.height;
            const ctx = composite.getContext('2d');
            if (!ctx) throw new Error('Could not get 2D context');
            ctx.drawImage(snapshot, 0, 0);
            const sx = snapshot.width / target.offsetWidth;
            const sy = snapshot.height / target.offsetHeight;
            for (const s of strokes) {
                ctx.strokeStyle = s.color;
                ctx.lineWidth = s.size * sx;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.beginPath();
                s.points.forEach((p, i) =>
                    i === 0 ? ctx.moveTo(p.x * sx, p.y * sy) : ctx.lineTo(p.x * sx, p.y * sy)
                );
                ctx.stroke();
            }
            const dataUrl = composite.toDataURL('image/png');
            const pngBase64 = dataUrl.split(',')[1] ?? '';
            const [owner, name] = repoId.split('/');
            const r = await apiCall<{ drawingId: string; rawUrl: string }>(
                `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/annotations`,
                {
                    method: 'POST',
                    body: { path, commitSha, pngBase64, strokes, width: target.offsetWidth, height: target.offsetHeight }
                }
            );
            onSaved({ ...r, previewDataUrl: dataUrl });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            {/* Overlay covers the page completely (inset:0). The SVG inside
             *  captures pointer events; text below is unreachable while
             *  annotate mode is on. */}
            <div className="annotate-overlay">
                {dims.width > 0 && (
                    <svg
                        ref={svgRef}
                        className="annotate-svg"
                        width={dims.width}
                        height={dims.height}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        {strokes.map((s, i) => (
                            <path
                                key={i}
                                d={pathFor(s)}
                                stroke={s.color}
                                strokeWidth={s.size}
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        ))}
                    </svg>
                )}
            </div>
            <div className="annotate-toolbar" role="toolbar" aria-label="Annotation tools">
                <div className="annotate-toolbar__row">
                    {PALETTE.map((c) => (
                        <button
                            key={c}
                            type="button"
                            className={'color-swatch' + (color === c ? ' color-swatch--active' : '')}
                            style={{ background: c }}
                            onClick={() => setColor(c)}
                            aria-label={`Color ${c}`}
                            disabled={saving}
                        />
                    ))}
                    <label className="draw-toolbar__size">
                        <input type="range" min={1} max={12} value={size} onChange={(e) => setSize(Number(e.target.value))} disabled={saving} />
                        {size}px
                    </label>
                </div>
                <div className="annotate-toolbar__row">
                    <button className="btn btn--ghost btn--small" onClick={() => setStrokes((s) => s.slice(0, -1))} disabled={saving || strokes.length === 0}>Undo</button>
                    <button className="btn btn--ghost btn--small" onClick={() => setStrokes([])} disabled={saving || strokes.length === 0}>Clear</button>
                    <button className="btn btn--ghost btn--small" onClick={onCancel} disabled={saving}>Cancel</button>
                    <button className="btn btn--primary btn--small" onClick={() => void save()} disabled={saving || strokes.length === 0}>
                        {saving ? 'Saving…' : 'Save annotation'}
                    </button>
                </div>
                {error && <div className="form-error">{error}</div>}
            </div>
        </>
    );
}
